// Implements: F-PRV-007, F-PRV-014
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { HIGHLIGHT_MAX_CHARS, highlight } from "../model/highlight";
import type { SourceLanguage, Token } from "../model/highlight";
import styles from "./SourceTabs.module.css";

export interface SourcePieces {
  readonly html: string;
  readonly css: string;
  readonly js: string;
}

/** Runs `callback` later and returns what cancels it. Injected so no test sleeps. */
export type Schedule = (callback: () => void, delayMs: number) => () => void;

const NOTICE_MS = 2000;

const defaultSchedule: Schedule = (callback, delayMs) => {
  const handle = setTimeout(callback, delayMs);
  return () => {
    clearTimeout(handle);
  };
};

type TabLabel = "preview.source.tab.html" | "preview.source.tab.css" | "preview.source.tab.js";

const TABS: readonly { readonly piece: SourceLanguage; readonly label: TabLabel }[] = [
  { piece: "html", label: "preview.source.tab.html" },
  { piece: "css", label: "preview.source.tab.css" },
  { piece: "js", label: "preview.source.tab.js" },
];

export interface SourceTabsProps {
  readonly pieces: SourcePieces;
  readonly schedule?: Schedule | undefined;
}

type Notice = "copied" | "failed" | null;

function renderTokens(tokens: readonly Token[]): ReactElement[] {
  return tokens.map((token, index) =>
    typeof token === "string" ? (
      <Fragment key={index}>{token}</Fragment>
    ) : (
      <span key={index} className={token.className}>
        {renderTokens(token.children)}
      </span>
    ),
  );
}

/**
 * A read-only display of the code of one version. Every piece is a React text child or a
 * class-only `<span>` built by `highlight`; the source is never given to the page as markup.
 */
export function SourceTabs({ pieces, schedule = defaultSchedule }: SourceTabsProps): ReactElement {
  const { t } = useTranslation();
  const [active, setActive] = useState<SourceLanguage>("html");
  const [notice, setNotice] = useState<Notice>(null);
  const cancel = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      cancel.current?.();
    },
    [],
  );

  const code = pieces[active];
  const tokens = useMemo(() => highlight(code, active), [code, active]);
  const tooLarge = code.length > HIGHLIGHT_MAX_CHARS;

  const copy = async (text: string): Promise<void> => {
    let next: Notice = "copied";
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      next = "failed";
    }
    cancel.current?.();
    setNotice(next);
    cancel.current = schedule(() => {
      cancel.current = null;
      setNotice(null);
    }, NOTICE_MS);
  };

  return (
    <section className={styles.source} aria-label={t("preview.source.title")}>
      <h3 className={styles.title}>{t("preview.source.title")}</h3>
      <div className={styles.bar}>
        <div role="tablist" aria-label={t("preview.source.tabs")} className={styles.tabs}>
          {TABS.map(({ piece, label }) => (
            <button
              key={piece}
              type="button"
              role="tab"
              id={`source-tab-${piece}`}
              aria-selected={active === piece}
              aria-controls="source-panel"
              className={styles.tab}
              onClick={() => {
                setActive(piece);
              }}
            >
              {t(label)}
            </button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void copy(code);
          }}
        >
          {t("preview.source.copy")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void copy(`${pieces.html}\n\n${pieces.css}\n\n${pieces.js}`);
          }}
        >
          {t("preview.source.copyAll")}
        </Button>
        <div role="status" className={styles.notice}>
          {notice === "copied" ? t("preview.source.copied") : null}
          {notice === "failed" ? t("preview.source.copyFailed") : null}
        </div>
      </div>
      <div role="tabpanel" id="source-panel" aria-labelledby={`source-tab-${active}`}>
        {tooLarge ? <p className={styles.hint}>{t("preview.source.plain")}</p> : null}
        {code === "" ? (
          <p className={styles.hint}>{t("preview.source.empty")}</p>
        ) : (
          <pre className={styles.code}>
            <code>{renderTokens(tokens)}</code>
          </pre>
        )}
      </div>
    </section>
  );
}
